import React from "react";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { InvestmentsMacroResponse } from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import {
  useInvestmentsAccessQuery,
  useInvestmentsDashboardQuery,
  useInvestmentsMacroQuery,
} from "@features/investments/queries";
import {
  MetricCard,
  formatCountShare,
  describeCurveRegime,
  findMacroSeries,
  formatBasisPointsNullable,
  formatPercentNullable,
  formatPercentPointNullable,
  worstRatePressureFamilies,
} from "@features/investments/screens/workspace-shared";
import "@views/investments/index.scss";

type RangePreset = "3M" | "1Y" | "3Y" | "5Y" | "10Y" | "Max";

const DEFAULT_SERIES_KEYS = ["EFFR", "3M", "2Y", "10Y", "30Y"];
const RANGE_PRESETS: RangePreset[] = ["3M", "1Y", "3Y", "5Y", "10Y", "Max"];
const SERIES_COLORS: Record<string, string> = {
  EFFR: "#f97316",
  "1M": "#38bdf8",
  "3M": "#22d3ee",
  "6M": "#14b8a6",
  "1Y": "#34d399",
  "2Y": "#60a5fa",
  "3Y": "#818cf8",
  "5Y": "#a78bfa",
  "7Y": "#f472b6",
  "10Y": "#f59e0b",
  "20Y": "#ef4444",
  "30Y": "#fb7185",
};

const RatesScreen = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();
  const dashboardQuery = useInvestmentsDashboardQuery();
  const macroQuery = useInvestmentsMacroQuery();
  const isOperator = accessQuery.data?.isGod === true;
  const [rangePreset, setRangePreset] = React.useState<RangePreset>("3Y");
  const [selectedSeriesKeys, setSelectedSeriesKeys] = React.useState<string[]>(DEFAULT_SERIES_KEYS);

  const loading = accessQuery.isLoading || macroQuery.isLoading || dashboardQuery.isLoading;
  const macro = macroQuery.data ?? null;
  const dashboard = dashboardQuery.data ?? null;
  const ratesSummary = dashboard?.equityRatesSummary ?? null;
  const pressuredFamilies = React.useMemo(() => worstRatePressureFamilies(ratesSummary, 3), [ratesSummary]);

  const effr = findMacroSeries(macro, "EFFR");
  const threeMonth = findMacroSeries(macro, "3M");
  const twoYear = findMacroSeries(macro, "2Y");
  const tenYear = findMacroSeries(macro, "10Y");
  const thirtyYear = findMacroSeries(macro, "30Y");
  const historyChartData = React.useMemo(
    () => buildMacroHistoryChartData(macro, selectedSeriesKeys, rangePreset),
    [macro, rangePreset, selectedSeriesKeys],
  );

  if (loading) {
    return <InvestmentsLoadingScreen message="Loading rates baseline..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  if (dashboardQuery.error) {
    return <InvestmentsPageState message={`Failed to load equity rates summary: ${dashboardQuery.error.message}`} />;
  }

  if (macroQuery.error || !macro) {
    return (
      <InvestmentsPageState
        message={`Failed to load rates baseline: ${macroQuery.error?.message || "macro cache unavailable"}. Run investments macro sync and investments macro recompute, then reload.`}
      />
    );
  }

  return (
    <InvestmentsWorkspaceShell
      title="Macro"
      description="Track the policy rate, Treasury curve, and hurdle-rate context alongside the research and portfolio workflow."
      generatedAt={macro.generatedAt}
    >
      <section className="workflow-section rates-curve-section">
        <div className="workflow-section-header">
          <div>
            <h2>Current Rate Snapshot</h2>
            <p>Use cash, intermediate, and long-duration benchmarks as a decision baseline, not as an automatic model override.</p>
          </div>
          <div className="workflow-header-controls">
            <Link className="workflow-header-controls-button" to="/economics">
              Open full economics system map
            </Link>
            <Link className="workflow-header-controls-button" to="/investments/coverage">
              Open Coverage
            </Link>
            {isOperator && (
              <Link className="workflow-header-controls-button" to="/investments/portfolio">
                Open Portfolio
              </Link>
            )}
          </div>
        </div>

        <div className="workflow-grid workflow-grid-macro-summary">
          <MetricCard label="Policy Target" value={formatPolicyTarget(macro)} />
          <MetricCard label="EFFR" value={formatPercentNullable(effr?.latestRate ?? null)} />
          <MetricCard label="3M Bill" value={formatPercentNullable(threeMonth?.latestRate ?? null)} />
          <MetricCard label="2Y Note" value={formatPercentNullable(twoYear?.latestRate ?? null)} />
          <MetricCard label="10Y Note" value={formatPercentNullable(tenYear?.latestRate ?? null)} />
          <MetricCard label="30Y Bond" value={formatPercentNullable(thirtyYear?.latestRate ?? null)} />
		  <MetricCard label="10Y-2Y" value={formatBasisPointsNullable(macro.spreads.twoYearTenYear ?? null)} />
		  <MetricCard label="10Y-3M" value={formatBasisPointsNullable(macro.spreads.threeMonthTenYear ?? null)} />
        </div>
      </section>

      {ratesSummary && (
        <section className="workflow-section">
          <div className="workflow-section-header">
            <div>
              <h2>What This Means For This Universe</h2>
              <p>Translate the current Treasury backdrop into cash-yield pressure across the tracked equity coverage set.</p>
            </div>
            <div className="workflow-section-meta">Coverage as of {ratesSummary.asOfDate || "N/A"}</div>
          </div>

          <section className="investments-card workflow-card workflow-rates-summary-card">
            <div className="workflow-rates-summary-grid">
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Beats 3M" variant="metric" /></div>
                <div className="workflow-metric-value">{formatCountShare(ratesSummary.beatingThreeMonthCount, ratesSummary.validCurrentFcfYieldCount)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Beats 10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatCountShare(ratesSummary.beatingTenYearCount, ratesSummary.validCurrentFcfYieldCount)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Median FCF-10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatBasisPointsNullable(ratesSummary.medianCurrentSpreadToTenYear)}</div>
              </article>
              <article className="workflow-rates-summary-metric">
                <div className="workflow-metric-label"><GlossaryTerm label="Median Buy Yld-10Y" variant="metric" /></div>
                <div className="workflow-metric-value">{formatBasisPointsNullable(ratesSummary.medianBuyThresholdSpreadToTenYear)}</div>
              </article>
            </div>
            <div className="workflow-rates-pressure-list">
              <div className="workflow-metric-label"><GlossaryTerm label="Most Pressured Families" variant="metric" /></div>
              {pressuredFamilies.length === 0 ? (
                <div className="workflow-empty">No family pressure data yet.</div>
              ) : (
                <div className="workflow-rates-pressure-items">
                  {pressuredFamilies.map((family) => (
                    <span key={family.key} className="workflow-rates-pressure-item">
                      {family.name}: {formatBasisPointsNullable(family.medianCurrentSpreadToTenYear)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        </section>
      )}

      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>Treasury Curve</h2>
            <p>Current Treasury constant maturity yields across bills, notes, and bonds.</p>
          </div>
          {macro.asOfDate && <div className="workflow-section-meta">As of {macro.asOfDate}</div>}
        </div>

        <div className="buy-levels-summary-grid rates-layout-grid">
          <section className="report-card rates-chart-card">
            <h3>Current Curve</h3>
            <div className="rates-chart-shell">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={macro.curveSnapshot.map((point) => ({ label: point.label, rate: point.rate }))}
                  margin={{ top: 34, right: 18, left: 6, bottom: 6 }}
                >
                  <CartesianGrid stroke="#18304f" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#c4d3f2", fontSize: 11 }} tickFormatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Tooltip
                    formatter={(value: number | null) => formatPercentNullable(value)}
                    contentStyle={{ background: "#0b1525", border: "1px solid #27456f", borderRadius: 14 }}
                  />
                  <Line type="monotone" dataKey="rate" stroke="#60a5fa" strokeWidth={3} dot={{ r: 4, fill: "#93c5fd" }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="report-card rates-table-card">
            <h3>Current Curve Table</h3>
            <div className="workflow-table-wrap">
              <table className="investments-table workflow-table">
                <thead>
                  <tr>
                    <th>Maturity</th>
                    <th>Yield</th>
                    <th>As of</th>
                  </tr>
                </thead>
                <tbody>
                  {macro.curveSnapshot.map((point) => (
                    <tr key={point.key}>
                      <td>{point.label}</td>
                      <td>{formatPercentNullable(point.rate)}</td>
                      <td>{point.asOfDate || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </section>

      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>History and Policy Moves</h2>
            <p>See how the curve evolved over time and where the Fed actually changed the policy range.</p>
          </div>
        </div>

        <section className="report-card rates-history-card">
          <div className="rates-history-controls">
            <div className="fundamentals-view-toggle buy-levels-view-toggle">
              {RANGE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={rangePreset === preset ? "active" : ""}
                  onClick={() => setRangePreset(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>
            <div className="rates-series-toggle">
              {macro.seriesHistory.map((series) => (
                <button
                  key={series.key}
                  type="button"
                  className={selectedSeriesKeys.includes(series.key) ? "active" : ""}
                  onClick={() => setSelectedSeriesKeys((current) => toggleSeriesSelection(current, series.key))}
                >
                  {series.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rates-chart-shell">
            {historyChartData.length === 0 || selectedSeriesKeys.length === 0 ? (
              <div className="workflow-empty">Select one or more rate series to view the historical chart.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={historyChartData} margin={{ top: 18, right: 18, left: 6, bottom: 6 }}>
                  <CartesianGrid stroke="#18304f" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#c4d3f2", fontSize: 11 }} tickFormatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Tooltip
                    formatter={(value, _name, item) => {
                      const key = typeof item?.dataKey === "string" ? item.dataKey : String(item?.dataKey ?? "");
                      const series = findMacroSeries(macro, key);
                      const numericValue = typeof value === "number" && Number.isFinite(value) ? value : null;
                      return [formatPercentNullable(numericValue), series?.label || key];
                    }}
                    contentStyle={{ background: "#0b1525", border: "1px solid #27456f", borderRadius: 14 }}
                  />
                  {selectedSeriesKeys.map((key) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={SERIES_COLORS[key] || "#c4d3f2"}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <div className="buy-levels-summary-grid rates-layout-grid">
          <section className="report-card">
            <h3>Policy Moves</h3>
            <div className="rates-policy-list">
              {macro.policyMoves.slice(0, 12).map((move) => (
                <article key={`${move.date}-${move.type}`} className="rates-policy-item">
                  <div className="rates-policy-top">
                    <strong>{move.type === "CUT" ? "Rate Cut" : "Rate Hike"}</strong>
                    <span>{move.date}</span>
                  </div>
                  <div className="rates-policy-detail">
                    <span>{Math.abs(move.changeBps)} bps</span>
                    <span>{formatPolicyMoveRange(move.targetLower, move.targetUpper)}</span>
                  </div>
                </article>
              ))}
              {macro.policyMoves.length === 0 && <div className="workflow-empty">No policy moves are available in the cached history.</div>}
            </div>
          </section>

          <section className="report-card">
            <h3>Interpretation</h3>
            <div className="dcf-lab-explainer-list">
              <article className="dcf-lab-explainer-item">
                <div className="dcf-lab-explainer-label">
                  Cash Alternative
                  <span>{formatPercentNullable(threeMonth?.latestRate ?? null)}</span>
                </div>
                <p>Short bills set the near-cash hurdle. Fresh equity ideas should comfortably beat this baseline over time to justify taking equity risk.</p>
              </article>
              <article className="dcf-lab-explainer-item">
                <div className="dcf-lab-explainer-label">
                  Long-Rate Hurdle
                  <span>{formatPercentNullable(tenYear?.latestRate ?? null)}</span>
                </div>
                <p>The 10Y Treasury is the clean long-duration comparison. When equity cash-flow yields compress toward it, margin for error gets thinner.</p>
              </article>
              <article className="dcf-lab-explainer-item">
                <div className="dcf-lab-explainer-label">
                  Curve Regime
                  <span>{describeCurveRegime(macro.curveRegime)}</span>
                </div>
                <p>{curveRegimeExplanation(macro)}</p>
              </article>
            </div>
          </section>
        </div>
      </section>
    </InvestmentsWorkspaceShell>
  );
};

function formatPolicyTarget(macro: InvestmentsMacroResponse): string {
  if (macro.policyRate.targetLower == null || macro.policyRate.targetUpper == null) {
    return "N/A";
  }
  return `${macro.policyRate.targetLower.toFixed(2)}% - ${macro.policyRate.targetUpper.toFixed(2)}%`;
}

function buildMacroHistoryChartData(macro: InvestmentsMacroResponse | null, selectedSeriesKeys: string[], preset: RangePreset): Array<Record<string, string | number | null>> {
  if (!macro || selectedSeriesKeys.length === 0) {
    return [];
  }
  const seriesMap = new Map(
    macro.seriesHistory
      .filter((series) => selectedSeriesKeys.includes(series.key))
      .map((series) => [series.key, series] as const),
  );
  const latestDate = latestAvailableDate(Array.from(seriesMap.values()));
  const startDate = startDateForPreset(latestDate, preset);
  const byDate = new Map<string, Record<string, string | number | null>>();
  for (const [key, series] of seriesMap.entries()) {
    for (const point of series.points) {
      if (startDate && point.date < startDate) {
        continue;
      }
      const row = byDate.get(point.date) || { date: point.date };
      row[key] = point.value;
      byDate.set(point.date, row);
    }
  }
  return Array.from(byDate.values()).sort((left, right) => String(left.date).localeCompare(String(right.date)));
}

function latestAvailableDate(seriesList: Array<InvestmentsMacroResponse["seriesHistory"][number]>): string {
  let latest = "";
  for (const series of seriesList) {
    if (series.latestDate > latest) {
      latest = series.latestDate;
    }
  }
  return latest;
}

function startDateForPreset(latestDate: string, preset: RangePreset): string {
  if (!latestDate || preset === "Max") {
    return "";
  }
  const parsed = new Date(`${latestDate}T00:00:00Z`);
  switch (preset) {
    case "3M":
      parsed.setUTCMonth(parsed.getUTCMonth() - 3);
      break;
    case "1Y":
      parsed.setUTCFullYear(parsed.getUTCFullYear() - 1);
      break;
    case "3Y":
      parsed.setUTCFullYear(parsed.getUTCFullYear() - 3);
      break;
    case "5Y":
      parsed.setUTCFullYear(parsed.getUTCFullYear() - 5);
      break;
    case "10Y":
      parsed.setUTCFullYear(parsed.getUTCFullYear() - 10);
      break;
    default:
      break;
  }
  return parsed.toISOString().slice(0, 10);
}

function toggleSeriesSelection(current: string[], key: string): string[] {
  if (current.includes(key)) {
    return current.filter((value) => value !== key);
  }
  return [...current, key];
}

function formatPolicyMoveRange(lower: number | null, upper: number | null): string {
  if (lower == null || upper == null) {
    return "Range N/A";
  }
  return `${lower.toFixed(2)}% - ${upper.toFixed(2)}%`;
}

function curveRegimeExplanation(macro: InvestmentsMacroResponse): string {
  switch (macro.curveRegime) {
    case "INVERTED":
	  return `One or both conventional long-minus-short curve spreads are negative (${formatPercentPointNullable(macro.spreads.twoYearTenYear)} 10Y-2Y, ${formatPercentPointNullable(macro.spreads.threeMonthTenYear)} 10Y-3M).`;
    case "FLAT":
	  return `The curve is positive but compressed (${formatPercentPointNullable(macro.spreads.twoYearTenYear)} 10Y-2Y, ${formatPercentPointNullable(macro.spreads.threeMonthTenYear)} 10Y-3M).`;
    default:
	  return `The curve is positively sloped with more breathing room (${formatPercentPointNullable(macro.spreads.twoYearTenYear)} 10Y-2Y, ${formatPercentPointNullable(macro.spreads.threeMonthTenYear)} 10Y-3M).`;
  }
}

export default RatesScreen;
